import { useCallback, useEffect, useRef, useState } from 'react';
import type { UserRole } from '@/types/common';
import type { GuidedFlowState } from '@/types/guidedFlow';
import type { WorkflowUserMessage } from '@/components/workflow/types';
import type { GuidedFlowRuntime } from '@/flows/runtime/types';
import { markOnboardingStepComplete, onboardingStepForFlow } from '@/services/onboardingProgress';
import { aiFilledKeysFromPartial } from '@/utils/draftPrefill';
import { consumeFlowPrefill } from '@/utils/flowPrefill';
import {
  storeConversationAttachment,
  toMessageAttachment,
} from '@/utils/conversationAttachment';
import { FLOW_CONFIRM_APPROVE_MESSAGE } from '@/utils/flowConfirmCta';

const CONFIRM_ACK_MS = 280;

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function storageKey(prefix: string, conversationId: string) {
  return `${prefix}:${conversationId}`;
}

function loadState<TDraft, TStep extends string, TResult>(
  runtime: GuidedFlowRuntime<TDraft, TStep, TResult>,
  conversationId: string
): GuidedFlowState<TDraft, TStep, TResult> | null {
  try {
    const raw = localStorage.getItem(storageKey(runtime.storageKeyPrefix, conversationId));
    if (!raw) return null;
    return runtime.hydrateStored(JSON.parse(raw) as GuidedFlowState<TDraft, TStep, TResult>);
  } catch {
    return null;
  }
}

function saveState<TDraft, TStep extends string, TResult>(
  runtime: GuidedFlowRuntime<TDraft, TStep, TResult>,
  state: GuidedFlowState<TDraft, TStep, TResult>
) {
  localStorage.setItem(storageKey(runtime.storageKeyPrefix, state.conversationId), JSON.stringify(state));
}

function buildProgressRail(
  steps: ReadonlyArray<{ id: string; label: string }>,
  activeIndex: number,
  visibleCount: number
) {
  const count = Math.min(visibleCount, steps.length);
  return steps.slice(0, count).map((s, idx) => ({
    id: s.id,
    label: s.label,
    status: idx < activeIndex ? ('completed' as const) : idx === activeIndex ? ('in-progress' as const) : ('pending' as const),
  }));
}

function allStepsCompletedRail(steps: ReadonlyArray<{ id: string; label: string }>) {
  return steps.map((s) => ({ id: s.id, label: s.label, status: 'completed' as const }));
}

function messageAttachmentKey(attachment?: WorkflowUserMessage['attachment']) {
  return attachment?.kind === 'csv' ? attachment.fileName : '';
}

function lastMessageMatchesInput(
  messages: WorkflowUserMessage[],
  content: string,
  attachment?: WorkflowUserMessage['attachment']
) {
  const last = messages[messages.length - 1];
  if (!last || last.content !== content) return false;
  return messageAttachmentKey(last.attachment) === messageAttachmentKey(attachment);
}

function buildThinkingSubmitState<TDraft, TStep extends string, TResult>(
  base: GuidedFlowState<TDraft, TStep, TResult>,
  userContent: string,
  loadingDraft: TDraft,
  attachment?: WorkflowUserMessage['attachment']
): GuidedFlowState<TDraft, TStep, TResult> {
  const alreadyStaged = lastMessageMatchesInput(base.userMessages, userContent, attachment);

  return {
    ...base,
    draft: loadingDraft,
    userMessages: alreadyStaged
      ? base.userMessages
      : [
          ...base.userMessages,
          {
            id: `user-${Date.now()}`,
            content: userContent,
            createdAt: new Date().toISOString(),
            attachment,
          },
        ],
    workflowStatus: 'validating',
    uiPhase: 'thinking',
    statusLabel: 'Thinking...',
    assistantHint: '',
  };
}

export interface UseGuidedFlowOptions {
  conversationId: string | undefined;
  enabled: boolean;
  siteLabel: string;
  userRole: UserRole;
  createdByEmail?: string;
}

export interface SubmitUserMessageOptions {
  attachment?: { kind: 'csv'; fileName: string };
  attachmentFile?: File | null;
}

export function useGuidedFlow<TDraft extends object, TStep extends string, TResult>(
  runtime: GuidedFlowRuntime<TDraft, TStep, TResult>,
  { conversationId, enabled, siteLabel, userRole, createdByEmail }: UseGuidedFlowOptions
) {
  const [state, setState] = useState<GuidedFlowState<TDraft, TStep, TResult> | null>(null);
  const simulationGen = useRef(0);
  const approveInFlightRef = useRef(false);

  const persist = useCallback(
    (next: GuidedFlowState<TDraft, TStep, TResult>) => {
      saveState(runtime, next);
      setState(next);
    },
    [runtime]
  );

  const runLoadingSequence = useCallback(
    async (
      base: GuidedFlowState<TDraft, TStep, TResult>,
      userContent: string,
      options?: SubmitUserMessageOptions
    ) => {
      const gen = ++simulationGen.current;

      const loadingDraft = runtime.resolveDraftForLoading
        ? runtime.resolveDraftForLoading(base.conversationId, base.draft)
        : base.draft;

      const progressSteps =
        runtime.resolveProgressRailSteps?.({
          conversationId: base.conversationId,
          draft: loadingDraft,
        }) ?? runtime.progressRailSteps;

      const withMessage = buildThinkingSubmitState(
        base,
        userContent,
        loadingDraft,
        options?.attachment
      );
      persist(withMessage);

      await delay(runtime.timing.thinkingMs);
      if (gen !== simulationGen.current) return;

      persist({
        ...withMessage,
        uiPhase: 'working',
        statusLabel: 'Working on it...',
        progressRail: buildProgressRail(progressSteps, 0, 1),
        assistantHint: "I'm working on your request",
      });

      for (let i = 0; i < progressSteps.length; i++) {
        const stepDelay = runtime.timing.progressStepMs[i] ?? runtime.timing.progressStepMs[0] ?? 750;
        await delay(stepDelay);
        if (gen !== simulationGen.current) return;

        const nextIndex = Math.min(i + 1, progressSteps.length - 1);
        const visibleCount = Math.min(i + 2, progressSteps.length);
        const isLast = i === progressSteps.length - 1;

        persist({
          ...withMessage,
          uiPhase: 'working',
          statusLabel: 'Working on it...',
          progressRail: isLast
            ? allStepsCompletedRail(progressSteps)
            : buildProgressRail(progressSteps, nextIndex, visibleCount),
          assistantHint: "I'm working on your request",
        });
      }

      await delay(runtime.timing.almostReadyMs);
      if (gen !== simulationGen.current) return;
      const rail = allStepsCompletedRail(progressSteps);
      persist({ ...withMessage, uiPhase: 'almost-ready', statusLabel: 'Almost ready..', progressRail: rail });

      await delay(runtime.timing.formRevealMs);
      if (gen !== simulationGen.current) return;

      persist({
        ...withMessage,
        ...runtime.buildFormReveal({ base: withMessage, siteLabel, userRole }),
        progressRail: rail,
        flowId: runtime.id,
        userRole,
      } as GuidedFlowState<TDraft, TStep, TResult>);
    },
    [persist, runtime, siteLabel, userRole]
  );

  useEffect(() => {
    if (!enabled || !conversationId) {
      setState(null);
      simulationGen.current += 1;
      return;
    }

    let next =
      loadState(runtime, conversationId) ?? runtime.createIdleState(conversationId, userRole);

    const prefill = consumeFlowPrefill<TDraft>(runtime.id, conversationId);
    if (prefill && Object.keys(prefill).length > 0) {
      const filled = aiFilledKeysFromPartial(prefill);
      next = {
        ...next,
        draft: { ...next.draft, ...prefill },
        aiFilledFields: filled,
        showAiBanner: filled.length > 0,
      };
      saveState(runtime, next);
    }

    setState(next);
  }, [conversationId, enabled, runtime, userRole]);

  const stagePendingUserMessage = useCallback(
    (content: string, attachment?: WorkflowUserMessage['attachment']) => {
      if (!conversationId) return false;

      const base =
        loadState(runtime, conversationId) ?? runtime.createIdleState(conversationId, userRole);

      if (base.uiPhase !== 'idle' || base.userMessages.length > 0) return false;

      persist({
        ...base,
        userMessages: [
          {
            id: `user-${conversationId}-pending`,
            content,
            createdAt: new Date().toISOString(),
            attachment,
          },
        ],
      });
      return true;
    },
    [conversationId, persist, runtime, userRole]
  );

  const submitUserMessage = useCallback(
    (raw: string, options?: SubmitUserMessageOptions) => {
      const persistAttachment = async () => {
        if (!options?.attachmentFile || !conversationId) {
          return options?.attachment ?? undefined;
        }
        await storeConversationAttachment(conversationId, options.attachmentFile);
        return toMessageAttachment({
          kind: 'csv',
          fileName: options.attachmentFile.name,
          mimeType: options.attachmentFile.type || 'text/csv',
        });
      };

      void (async () => {
        const text = raw.trim() || (options?.attachment ? 'See attached file.' : '');
        if (!text || !conversationId) return;

        let attachment = options?.attachment;
        if (!attachment && options?.attachmentFile) {
          attachment = await persistAttachment();
        }

        const base =
          loadState(runtime, conversationId) ??
          runtime.createIdleState(conversationId, userRole);

        if (base.uiPhase === 'form') {
          const patchedDraft = runtime.patchDraftOnFormMessage
            ? runtime.patchDraftOnFormMessage(base.draft, userRole)
            : base.draft;

          persist({
            ...base,
            userMessages: [
              ...base.userMessages,
              {
                id: `user-${Date.now()}`,
                content: text,
                createdAt: new Date().toISOString(),
                attachment,
              },
            ],
            assistantHint: runtime.formMessageAssistantHint,
            draft: patchedDraft,
          });
          return;
        }

        if (base.uiPhase === 'saving') return;

        if (base.uiPhase === 'success') {
          persist({
            ...base,
            userMessages: [
              ...base.userMessages,
              {
                id: `user-${Date.now()}`,
                content: text,
                createdAt: new Date().toISOString(),
                attachment,
              },
            ],
          });
          return;
        }

        const loadingDraft = runtime.resolveDraftForLoading
          ? runtime.resolveDraftForLoading(conversationId, base.draft)
          : base.draft;

        const withMessage = buildThinkingSubmitState(base, text, loadingDraft, attachment);

        if (runtime.shouldSkipLoadingSequence?.(conversationId, loadingDraft)) {
          persist({
            ...withMessage,
            ...runtime.buildFormReveal({ base: withMessage, siteLabel, userRole }),
            progressRail: runtime.progressRailSteps.map((s) => ({
              id: s.id,
              label: s.label,
              status: 'completed' as const,
            })),
            flowId: runtime.id,
            userRole,
          } as GuidedFlowState<TDraft, TStep, TResult>);
          return;
        }

        if (
          base.uiPhase === 'thinking' &&
          lastMessageMatchesInput(base.userMessages, text, attachment)
        ) {
          void runLoadingSequence(base, text, { attachment });
          return;
        }

        persist(withMessage);
        void runLoadingSequence(withMessage, text, { attachment });
      })();
    },
    [conversationId, persist, runLoadingSequence, runtime, siteLabel, userRole]
  );

  const updateDraft = useCallback(
    (patch: Partial<TDraft>) => {
      setState((current) => {
        if (!current) return current;
        const next = { ...current, draft: { ...current.draft, ...patch } };
        saveState(runtime, next);
        return next;
      });
    },
    [runtime]
  );

  const goToFormStep = useCallback(
    (step: TStep) => {
      setState((current) => {
        if (!current) return current;
        const draft = runtime.ensureDraftForFormStep
          ? runtime.ensureDraftForFormStep(current.draft, step)
          : current.draft;
        const next = {
          ...current,
          draft,
          formStep: step,
          uiPhase: 'form' as const,
          showFormWizard: true,
        };
        saveState(runtime, next);
        return next;
      });
    },
    [runtime]
  );

  const nextFormStep = useCallback(() => {
    setState((current) => {
      if (!current) return current;
      const idx = runtime.formStepOrder.indexOf(current.formStep);
      if (idx >= runtime.formStepOrder.length - 1) return current;
      const step = runtime.formStepOrder[idx + 1];
      const draft = runtime.ensureDraftForFormStep
        ? runtime.ensureDraftForFormStep(current.draft, step)
        : current.draft;
      const next = { ...current, draft, formStep: step };
      saveState(runtime, next);
      return next;
    });
  }, [runtime]);

  const prevFormStep = useCallback(() => {
    setState((current) => {
      if (!current) return current;
      const idx = runtime.formStepOrder.indexOf(current.formStep);
      if (idx <= 0) return current;
      const next = { ...current, formStep: runtime.formStepOrder[idx - 1] };
      saveState(runtime, next);
      return next;
    });
  }, [runtime]);

  const toggleExplainability = useCallback(() => {
    setState((current) => {
      if (!current) return current;
      const next = { ...current, showExplainability: !current.showExplainability };
      saveState(runtime, next);
      return next;
    });
  }, [runtime]);

  const dismissAiBanner = useCallback(() => {
    setState((current) => {
      if (!current) return current;
      const next = { ...current, showAiBanner: false };
      saveState(runtime, next);
      return next;
    });
  }, [runtime]);

  const approveAndExecute = useCallback(async () => {
    if (!conversationId || approveInFlightRef.current) return;

    const current = loadState(runtime, conversationId);
    if (!current || current.uiPhase === 'saving' || current.uiPhase === 'success') return;

    approveInFlightRef.current = true;

    try {
      const approveText =
        runtime.buildApproveUserMessage?.(current.draft) ?? FLOW_CONFIRM_APPROVE_MESSAGE;
      const alreadyRecorded = current.userMessages.some(
        (m) => m.id.startsWith('approve-') && m.content === approveText
      );

      if (!alreadyRecorded) {
        const withApprove: GuidedFlowState<TDraft, TStep, TResult> = {
          ...current,
          userMessages: [
            ...current.userMessages,
            {
              id: `approve-${Date.now()}`,
              content: approveText,
              createdAt: new Date().toISOString(),
            },
          ],
        };
        persist(withApprove);
        await delay(CONFIRM_ACK_MS);
      }

      const latest =
        loadState(runtime, conversationId) ??
        current;
      if (latest.uiPhase === 'saving' || latest.uiPhase === 'success') return;

      const savingState: GuidedFlowState<TDraft, TStep, TResult> = {
        ...latest,
        uiPhase: 'saving',
        statusLabel: runtime.savingTitle ?? 'Saving...',
        workflowStatus: 'executing',
      };
      persist(savingState);

      await delay(runtime.timing.savingMs);

      const success = runtime.buildSuccess(savingState.draft, savingState.userRole);
      await runtime.onApproved?.({
        draft: savingState.draft,
        userRole: savingState.userRole,
        actorEmail: createdByEmail,
      });

      if (savingState.userRole === 'novice') {
        const stepId = onboardingStepForFlow(runtime.id);
        if (stepId) markOnboardingStepComplete(stepId);
      }

      persist({
        ...savingState,
        uiPhase: 'success',
        statusLabel: 'Complete',
        workflowStatus: 'completed',
        result: success.result,
        assistantHint: success.assistantHint || savingState.assistantHint || runtime.formMessageAssistantHint,
        introAssistantHint:
          savingState.introAssistantHint ?? savingState.assistantHint ?? runtime.formMessageAssistantHint,
        showAiBanner: false,
      });
    } finally {
      approveInFlightRef.current = false;
    }
  }, [conversationId, createdByEmail, persist, runtime]);

  const appendUserActionMessage = useCallback(
    (content: string, messageId = `action-${Date.now()}`): string | null => {
      if (!conversationId) return null;

      const trimmed = content.trim();
      if (!trimmed) return null;

      const current = loadState(runtime, conversationId);
      if (!current) return null;

      persist({
        ...current,
        userMessages: [
          ...current.userMessages,
          {
            id: messageId,
            content: trimmed,
            createdAt: new Date().toISOString(),
          },
        ],
      });
      return messageId;
    },
    [conversationId, persist, runtime]
  );

  const isConfirmReady = runtime.isConfirmReady(state?.draft ?? runtime.emptyDraft());
  const aiFilledFields = state
    ? state.aiFilledFields.length > 0
      ? state.aiFilledFields
      : runtime.getAiFilledFields(state.draft, state.userRole)
    : [];
  const statusLabel = state?.statusLabel ?? '';

  return {
    state,
    statusLabel,
    aiFilledFields,
    isConfirmReady,
    submitUserMessage,
    stagePendingUserMessage,
    updateDraft,
    goToFormStep,
    nextFormStep,
    prevFormStep,
    toggleExplainability,
    dismissAiBanner,
    approveAndExecute,
    appendUserActionMessage,
  };
}

